// src/services/claimRecordService.js
//
// Paso 1 de la restructuración del flujo de claims (ver memoria del
// proyecto: claim_lifecycle_restructure.md): el "claim padre" — solo datos
// de identidad/caso (cliente, póliza, causa, ajustador), sin nada
// financiero. Un claimRecord puede tener múltiples "checks" (cheques/pagos)
// hijos en la colección `checks`, cada uno con su propio Closing Statement.
//
// La colección vieja `claims` (claimService.js) queda congelada como
// respaldo — este servicio y `checks` son el modelo nuevo, separado, para
// no arriesgar los datos en producción mientras se migra (ver Fase 1B).
import { db } from '../firebase/config';
import { appendActivity } from './workspaceService';
import { buildInitialFlow } from './flowService';
import { buildAccessNames, mergeById } from '../utils/claimAccess';
import { getAdjusters } from './adjusterService';
import {
  collection,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';

const CLAIM_RECORDS_COLLECTION = 'claimRecords';

// Dueño en blanco — forma de owner2/owner3 (owner1 sigue siendo
// clientName/clientEmail/clientPhone, ya usados en todo el codebase; no se
// tocan para no romper nada existente).
const emptyOwner = () => ({ firstName: '', lastName: '', phone: '', email: '' });

// Campos nuevos pedidos por el cliente (spec 2026-09-17, módulos 1-3:
// propiedad/dueños, aseguradora/agente/ajustador, detalles financieros y
// mortgage) — todos opcionales, con default vacío, para que un claimRecord
// viejo (sin estos campos) siga funcionando igual en todas partes.
const buildExtendedFields = (data) => ({
  // Módulo 1: propiedad y dueños adicionales
  propertyType: data.propertyType || '', // 'residential' | 'commercial'
  owner2: data.owner2 || emptyOwner(),
  owner3: data.owner3 || emptyOwner(),
  lossAddressStreet: data.lossAddressStreet || '',
  lossAddressCity: data.lossAddressCity || '',
  lossAddressState: data.lossAddressState || '',
  lossAddressZip: data.lossAddressZip || '',
  mailingSameAsLoss: data.mailingSameAsLoss !== false,
  mailingAddressStreet: data.mailingAddressStreet || '',
  mailingAddressCity: data.mailingAddressCity || '',
  mailingAddressState: data.mailingAddressState || '',
  mailingAddressZip: data.mailingAddressZip || '',

  // Módulo 2: aseguradora, agente, ajustador de la aseguradora
  insuranceClaimEmail: data.insuranceClaimEmail || '',
  insurancePhone: data.insurancePhone || '',
  agentName: data.agentName || '',
  agentAddress: data.agentAddress || '',
  agentPhone: data.agentPhone || '',
  // OJO: `adjusterName` (ya existente) es nuestro Public Adjuster — esto es
  // el ajustador DE LA ASEGURADORA, un rol distinto.
  insuranceAdjusterName: data.insuranceAdjusterName || '',
  insuranceAdjusterPhone: data.insuranceAdjusterPhone || '',
  insuranceAdjusterEmail: data.insuranceAdjusterEmail || '',

  // Módulo 3: coberturas, deducible, colaboradores, mortgage
  coverageA: data.coverageA ?? null,
  coverageB: data.coverageB ?? null,
  coverageC: data.coverageC ?? null,
  coverageD: data.coverageD ?? null,
  deductibleAmount: data.deductibleAmount ?? null,
  deductibleType: data.deductibleType || 'fixed', // 'fixed' | 'percentage'
  // P.A Assigned Adjuster: además del principal (adjusterName), otros PA
  // asignados. Other Adjuster Access: colaboradores con acceso. Ver
  // utils/claimAccess.js — con esto se calcula accessNames al guardar.
  collaboratorAdjusters: Array.isArray(data.collaboratorAdjusters) ? data.collaboratorAdjusters : [],
  otherAccess: Array.isArray(data.otherAccess) ? data.otherAccess.map(n => String(n).trim()).filter(Boolean) : [],
  mortgageCompanyName: data.mortgageCompanyName || '',
  mortgageLoanNumber: data.mortgageLoanNumber || '',
  mortgagePhone: data.mortgagePhone || '',
  // Depreciación recuperable del estimado y cuándo se pidió (utils/recoverableDepreciation.js).
  recoverableDepreciation: data.recoverableDepreciation === '' || data.recoverableDepreciation === undefined || data.recoverableDepreciation === null ? null : Number(data.recoverableDepreciation),
  rdRequestedDate: data.rdRequestedDate || '',

  // Datos básicos pedidos en la reunión del 22-sep y por la LOR/SPOL
  // (ANALISIS_DOCUMENTOS_LOR_SPOL.md). Mismos nombres que usan
  // documents/lorData.js y documents/spolData.js.
  agreedFeePercent: data.agreedFeePercent === '' || data.agreedFeePercent === undefined ? null : Number(data.agreedFeePercent),
  clientMobile: data.clientMobile || '',
  commercialEntityName: data.commercialEntityName || '',
  commercialEntityEmail: data.commercialEntityEmail || '',
  commercialEntityPhone: data.commercialEntityPhone || '',
  lossTime: data.lossTime || '',
  lossDescription: data.lossDescription || '',
  claimType: data.claimType || '', // 'emergency' | 'non_emergency' | 'supplemental'

  // Póliza y seguro (segundo paso del alta, se puede completar después)
  policyEffectiveDate: data.policyEffectiveDate || '',
  policyExpirationDate: data.policyExpirationDate || '',
  agentAgency: data.agentAgency || '',
  insuranceCompanyAddress: data.insuranceCompanyAddress || '',
  occupancy: data.occupancy || '',
  insuredInterest: data.insuredInterest || '',
  deskAdjusterName: data.deskAdjusterName || '',
  deskAdjusterPhone: data.deskAdjusterPhone || '',
  deskAdjusterEmail: data.deskAdjusterEmail || '',

  // Personas del reclamo (reunión 17-sep con Luis) y contactos adicionales
  // del cliente (reunión 22-sep: "permitir agregar múltiples contactos").
  leadSource: data.leadSource || '',        // quién trajo el lead
  signatureObtainedBy: data.signatureObtainedBy || '',
  negotiator: data.negotiator || '',
  mitigationCompany: data.mitigationCompany || '',
  additionalContacts: Array.isArray(data.additionalContacts)
    ? data.additionalContacts.map(c => ({ name: (c.name || '').trim(), relation: (c.relation || '').trim(), phone: (c.phone || '').trim(), email: (c.email || '').trim() }))
      .filter(c => c.name || c.phone || c.email)
    : [],

  // Se cargan desde los pasos del flujo (config/claimFlow.js → fields con target 'claim')
  coverageDecision: data.coverageDecision || '', // approved | partial_denial | below_deductible | denied
  attorneyName: data.attorneyName || '',
  attorneyEmail: data.attorneyEmail || '',

  // Traídos de Organized Adjusting (migración 2026-09-24).
  contractSignedDate: data.contractSignedDate || '',
  dateCalledIn: data.dateCalledIn || '',
  vendor: data.vendor || ''
});

const ACCESS_KEYS = ['adjusterName', 'collaboratorAdjusters', 'otherAccess'];
const sameList = (a = [], b = []) => a.length === b.length && a.every((v, i) => v === b[i]);

// Copia accessNames a los cheques y citas del reclamo (las reglas y las
// consultas del PA los leen de cada documento).
const syncAccessToChildren = async (claimRecordId, accessNames) => {
  const [checks, events, summaries] = await Promise.all([
    getDocs(query(collection(db, 'checks'), where('claimRecordId', '==', claimRecordId))),
    getDocs(query(collection(db, 'claimEvents'), where('claimRecordId', '==', claimRecordId))),
    // Espejo para el CSR Assistant: si aún no existe o no se puede leer, no frena lo demás.
    getDocs(query(collection(db, 'checkSummaries'), where('claimRecordId', '==', claimRecordId))).catch(() => ({ docs: [] }))
  ]);
  const stale = [...checks.docs, ...events.docs, ...summaries.docs].filter(item => !sameList(item.data().accessNames, accessNames));
  for (let i = 0; i < stale.length; i += 400) {
    const batch = writeBatch(db);
    stale.slice(i, i + 400).forEach(item => batch.update(item.ref, { accessNames }));
    await batch.commit();
  }
};

// Recalcula el acceso de todos los reclamos y lo copia a sus cheques y
// citas (al enlazar o quitar un asistente, y una vez al migrar producción:
// los documentos viejos no tienen accessNames). Solo el equipo CSR. Lee las
// tres colecciones una sola vez y escribe por lotes solo lo que cambió.
// Devuelve cuántos reclamos cambiaron y cuántos documentos se escribieron.
export const syncAllClaimAccess = async (adjusters = null) => {
  const list = adjusters || await getAdjusters();
  const [claims, checks, events, summaries] = await Promise.all([
    getDocs(collection(db, CLAIM_RECORDS_COLLECTION)),
    getDocs(collection(db, 'checks')),
    getDocs(collection(db, 'claimEvents')),
    getDocs(collection(db, 'checkSummaries'))
  ]);
  const accessById = new Map();
  const writes = [];
  claims.docs.forEach(item => {
    const accessNames = buildAccessNames(item.data(), list);
    accessById.set(item.id, accessNames);
    if (!sameList(item.data().accessNames, accessNames)) writes.push({ ref: item.ref, accessNames, claim: true });
  });
  [...checks.docs, ...events.docs, ...summaries.docs].forEach(item => {
    const accessNames = accessById.get(item.data().claimRecordId);
    if (accessNames && !sameList(item.data().accessNames, accessNames)) writes.push({ ref: item.ref, accessNames });
  });
  for (let i = 0; i < writes.length; i += 400) {
    const batch = writeBatch(db);
    writes.slice(i, i + 400).forEach(w => batch.update(w.ref, { accessNames: w.accessNames }));
    await batch.commit();
  }
  return { claims: writes.filter(w => w.claim).length, documents: writes.length };
};

// Igual que syncAllClaimAccess pero solo para los reclamos de UN P.A en su
// compañía: lo usa el P.A (o su P.A Admin) al crear o quitar un asistente, ya
// que no puede leer las colecciones completas. Las consultas llevan los dos
// filtros que exigen las reglas (companyName + adjusterName).
export const syncAccessForAdjuster = async (adjusterName, companyName, adjusters = null) => {
  const list = adjusters || await getAdjusters();
  const scoped = name => query(collection(db, name), where('companyName', '==', companyName), where('adjusterName', '==', adjusterName));
  const [claims, checks, events, summaries] = await Promise.all([
    getDocs(scoped(CLAIM_RECORDS_COLLECTION)),
    getDocs(scoped('checks')),
    getDocs(scoped('claimEvents')),
    getDocs(scoped('checkSummaries'))
  ]);
  const accessById = new Map();
  const writes = [];
  claims.docs.forEach(item => {
    const accessNames = buildAccessNames(item.data(), list);
    accessById.set(item.id, accessNames);
    if (!sameList(item.data().accessNames, accessNames)) writes.push({ ref: item.ref, accessNames, claim: true });
  });
  [...checks.docs, ...events.docs, ...summaries.docs].forEach(item => {
    const accessNames = accessById.get(item.data().claimRecordId);
    if (accessNames && !sameList(item.data().accessNames, accessNames)) writes.push({ ref: item.ref, accessNames });
  });
  for (let i = 0; i < writes.length; i += 400) {
    const batch = writeBatch(db);
    writes.slice(i, i + 400).forEach(w => batch.update(w.ref, { accessNames: w.accessNames }));
    await batch.commit();
  }
  return { claims: writes.filter(w => w.claim).length, documents: writes.length };
};

// options (importación de Organized): withFlow false = nace sin paso (se lo
// pone la migración con su historial); adjusters evita releer la colección
// en cada reclamo; silent no llena "Actividad" con cientos de altas.
export const createClaimRecord = async (data, userEmail, userRole = 'admin', options = {}) => {
  const now = Timestamp.now();
  const payload = {
    claimNumber: data.claimNumber || '',
    clientName: data.clientName || '',
    companyName: data.companyName || '',
    adjusterName: data.adjusterName || '',
    incidentDate: data.incidentDate || null,
    policyNumber: data.policyNumber || '',
    cause: data.cause || '',
    crmClaimId: data.crmClaimId || '',
    insuranceCompanyName: data.insuranceCompanyName || '',
    // Datos de "Cliente y P.A." — vivían en el Paso 2 (Closing Statement),
    // se movieron acá para no duplicar la captura de datos (ver
    // claim_lifecycle_restructure.md, simplificación previa
    // al envío a firmar).
    clientEmail: data.clientEmail || '',
    clientPhone: data.clientPhone || '',
    clientAddress: data.clientAddress || '',
    ...buildExtendedFields(data),
    accessNames: [],
    // Todo reclamo nuevo nace en el primer paso del flujo (Prospecto) — ver
    // config/claimFlow.js. updateClaimRecord no toca `flow` (whitelist).
    flow: options.withFlow === false ? null : buildInitialFlow('claim', userEmail, userRole),
    createdAt: now,
    updatedAt: now,
    createdBy: userEmail
  };
  payload.accessNames = buildAccessNames(payload, options.adjusters || await getAdjusters());
  const ref = doc(collection(db, CLAIM_RECORDS_COLLECTION));
  const batch = writeBatch(db);
  batch.set(ref, payload);
  if (!options.silent) appendActivity(batch, { event: 'claim_record_created', targetId: ref.id, reference: `Claim ${payload.claimNumber || ref.id}` });
  await batch.commit();
  return { id: ref.id, ...payload };
};

export const updateClaimRecord = async (claimRecordId, data) => {
  const ref = doc(db, CLAIM_RECORDS_COLLECTION, claimRecordId);
  const payload = {
    claimNumber: data.claimNumber,
    clientName: data.clientName,
    companyName: data.companyName,
    adjusterName: data.adjusterName,
    incidentDate: data.incidentDate || null,
    policyNumber: data.policyNumber || '',
    cause: data.cause,
    crmClaimId: data.crmClaimId,
    insuranceCompanyName: data.insuranceCompanyName || '',
    clientEmail: data.clientEmail || '',
    clientPhone: data.clientPhone || '',
    clientAddress: data.clientAddress || '',
    ...buildExtendedFields(data),
    updatedAt: Timestamp.now()
  };
  // Solo se escriben los campos que vienen en `data`: los que se cargan en
  // otros lugares (p. ej. el abogado o la decisión de cobertura, desde los
  // pasos del flujo) no deben quedar en blanco al editar el formulario.
  Object.keys(payload).forEach(key => { if (key !== 'updatedAt' && !(key in data)) delete payload[key]; });
  // Solo si cambió quién tiene acceso se recalcula accessNames (un PA que no
  // es supervisor no puede tocar esos campos — firestore.rules).
  let accessChanged = false;
  if (ACCESS_KEYS.some(key => key in payload)) {
    const before = (await getDoc(ref)).data() || {};
    const fieldChanged = ACCESS_KEYS.some(key => key in payload
      && JSON.stringify(before[key] ?? (key === 'adjusterName' ? '' : [])) !== JSON.stringify(payload[key]));
    if (!fieldChanged) {
      ACCESS_KEYS.forEach(key => { delete payload[key]; });
    } else {
      const accessNames = buildAccessNames({ ...before, ...payload }, await getAdjusters());
      if (!sameList(before.accessNames, accessNames)) {
        payload.accessNames = accessNames;
        accessChanged = true;
      }
    }
  }
  await updateDoc(ref, payload);
  if (accessChanged) await syncAccessToChildren(claimRecordId, payload.accessNames);
  return { id: claimRecordId, ...payload };
};

// Actualización parcial desde el formulario de un paso (StepForm): solo los
// campos del reclamo declarados en config/claimFlow.js con target 'claim'.
export const updateClaimRecordFields = async (claimRecordId, changes, allowedKeys) => {
  const payload = {};
  Object.entries(changes).forEach(([key, value]) => {
    if (allowedKeys.includes(key)) payload[key] = value ?? '';
  });
  if (Object.keys(payload).length === 0) return {};
  await updateDoc(doc(db, CLAIM_RECORDS_COLLECTION, claimRecordId), { ...payload, updatedAt: Timestamp.now() });
  return payload;
};

export const getClaimRecordById = async claimRecordId => {
  const snapshot = await getDoc(doc(db, CLAIM_RECORDS_COLLECTION, claimRecordId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

export const getAllClaimRecords = async () => {
  const snapshot = await getDocs(query(collection(db, CLAIM_RECORDS_COLLECTION), orderBy('createdAt', 'desc')));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
};

export const getClaimRecordsByCompany = async companyName => {
  const snapshot = await getDocs(query(collection(db, CLAIM_RECORDS_COLLECTION), where('companyName', '==', companyName)));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
};

// Varias compañías a la vez (CSR Assistant): una consulta por compañía, que es lo
// que las reglas pueden autorizar; se unen sin repetir.
export const getClaimRecordsByCompanies = async companies => {
  const lists = await Promise.all((companies || []).map(company => getClaimRecordsByCompany(company)));
  return mergeById(lists.flat());
};

// Para un ajustador SIN permiso de ver toda la empresa: las reglas de
// Firestore solo aceptan la consulta si también filtra por su nombre (si
// no, se rechaza entera). Mismo criterio que getChecksForDirectoryByCompanyAndAdjuster.
// Trae los reclamos donde es PA principal y los que le dan acceso por
// accessNames (otro PA asignado, Other Adjuster Access o asistente).
export const getClaimRecordsByCompanyAndAdjuster = async (companyName, adjusterName) => {
  const base = collection(db, CLAIM_RECORDS_COLLECTION);
  const [own, shared] = await Promise.all([
    getDocs(query(base, where('companyName', '==', companyName), where('adjusterName', '==', adjusterName))),
    getDocs(query(base, where('companyName', '==', companyName), where('accessNames', 'array-contains', adjusterName)))
  ]);
  return mergeById([...own.docs, ...shared.docs].map(item => ({ id: item.id, ...item.data() })));
};

export const deleteClaimRecord = claimRecordId => deleteDoc(doc(db, CLAIM_RECORDS_COLLECTION, claimRecordId));

export { CLAIM_RECORDS_COLLECTION };
